import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clientsApi } from '../api';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';

export function ClientFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    contact: {
      referent: '',
      email: '',
      phone: '',
      address: '',
    },
    notes: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Carica dati esistenti se in modifica
  const { data: clientData, isLoading: isLoadingClient } = useQuery({
    queryKey: ['client', id],
    queryFn: () => clientsApi.get(id!),
    enabled: isEditing,
  });

  useEffect(() => {
    if (clientData?.data.data.client) {
      const client = clientData.data.data.client;
      setFormData({
        code: client.code,
        name: client.name,
        description: client.description || '',
        contact: {
          referent: client.contact?.referent || '',
          email: client.contact?.email || '',
          phone: client.contact?.phone || '',
          address: client.contact?.address || '',
        },
        notes: client.notes || '',
      });
    }
  }, [clientData]);

  // Mutation per creazione
  const createMutation = useMutation({
    mutationFn: (data: typeof formData) => clientsApi.create({
      code: data.code,
      name: data.name,
      description: data.description || undefined,
      contact: Object.values(data.contact).some(v => v) ? data.contact : undefined,
      notes: data.notes || undefined,
    }),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      navigate(`/clients/${response.data.data.client.id}`);
    },
    onError: (error: Error) => {
      setErrors({ submit: error.message });
    },
  });

  // Mutation per aggiornamento
  const updateMutation = useMutation({
    mutationFn: (data: typeof formData) => clientsApi.update(id!, {
      name: data.name,
      description: data.description || undefined,
      contact: Object.values(data.contact).some(v => v) ? data.contact : undefined,
      notes: data.notes || undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['client', id] });
      navigate(`/clients/${id}`);
    },
    onError: (error: Error) => {
      setErrors({ submit: error.message });
    },
  });

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.code.trim()) {
      newErrors.code = 'Il codice è obbligatorio';
    } else if (!/^[A-Z0-9_-]+$/i.test(formData.code)) {
      newErrors.code = 'Il codice può contenere solo lettere, numeri, trattini e underscore';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Il nome è obbligatorio';
    }

    if (!formData.contact.email.trim()) {
      newErrors['contact.email'] = 'L\'email è obbligatoria';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact.email)) {
      newErrors['contact.email'] = 'Email non valida';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    if (isEditing) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleChange = (field: string, value: string) => {
    if (field.startsWith('contact.')) {
      const contactField = field.replace('contact.', '');
      setFormData(prev => ({
        ...prev,
        contact: { ...prev.contact, [contactField]: value },
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
    // Rimuovi errore quando l'utente modifica il campo
    if (errors[field]) {
      setErrors(prev => {
        const { [field]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  if (isEditing && isLoadingClient) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Indietro
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditing ? 'Modifica cliente' : 'Nuovo cliente'}
        </h1>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {errors.submit && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
            {errors.submit}
          </div>
        )}

        {/* Dati principali */}
        <div className="card p-6 space-y-4">
          <h2 className="font-semibold text-gray-900 mb-4">Dati principali</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="code" className="label">
                Codice <span className="text-red-500">*</span>
              </label>
              <input
                id="code"
                type="text"
                value={formData.code}
                onChange={(e) => handleChange('code', e.target.value.toUpperCase())}
                disabled={isEditing}
                placeholder="ES. MUSEO-BLU"
                className={`input ${errors.code ? 'border-red-500' : ''} ${isEditing ? 'bg-gray-100' : ''}`}
              />
              {errors.code && (
                <p className="text-red-500 text-xs mt-1">{errors.code}</p>
              )}
              {isEditing && (
                <p className="text-gray-500 text-xs mt-1">Il codice non può essere modificato</p>
              )}
            </div>

            <div>
              <label htmlFor="name" className="label">
                Nome <span className="text-red-500">*</span>
              </label>
              <input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Nome del cliente"
                className={`input ${errors.name ? 'border-red-500' : ''}`}
              />
              {errors.name && (
                <p className="text-red-500 text-xs mt-1">{errors.name}</p>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="description" className="label">
              Descrizione
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={3}
              placeholder="Breve descrizione del cliente"
              className="input"
            />
          </div>
        </div>

        {/* Contatti */}
        <div className="card p-6 space-y-4">
          <h2 className="font-semibold text-gray-900 mb-4">Contatti</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="referent" className="label">
                Referente
              </label>
              <input
                id="referent"
                type="text"
                value={formData.contact.referent}
                onChange={(e) => handleChange('contact.referent', e.target.value)}
                placeholder="Nome del referente"
                className="input"
              />
            </div>

            <div>
              <label htmlFor="email" className="label">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                id="email"
                type="email"
                value={formData.contact.email}
                onChange={(e) => handleChange('contact.email', e.target.value)}
                placeholder="email@esempio.it"
                className={`input ${errors['contact.email'] ? 'border-red-500' : ''}`}
              />
              {errors['contact.email'] && (
                <p className="text-red-500 text-xs mt-1">{errors['contact.email']}</p>
              )}
            </div>

            <div>
              <label htmlFor="phone" className="label">
                Telefono
              </label>
              <input
                id="phone"
                type="tel"
                value={formData.contact.phone}
                onChange={(e) => handleChange('contact.phone', e.target.value)}
                placeholder="+39 ..."
                className="input"
              />
            </div>

            <div>
              <label htmlFor="address" className="label">
                Indirizzo
              </label>
              <input
                id="address"
                type="text"
                value={formData.contact.address}
                onChange={(e) => handleChange('contact.address', e.target.value)}
                placeholder="Via, Città"
                className="input"
              />
            </div>
          </div>
        </div>

        {/* Note */}
        <div className="card p-6">
          <h2 className="font-semibold text-gray-900 mb-4">Note interne</h2>
          <textarea
            id="notes"
            value={formData.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            rows={4}
            placeholder="Note visibili solo agli operatori"
            className="input"
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="btn-secondary"
          >
            Annulla
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="btn-primary"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Salvataggio...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {isEditing ? 'Salva modifiche' : 'Crea cliente'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
